import { Link } from "react-router-dom";
import {
  ArrowRight,
  Hotel,
  Building2,
  Home,
  Utensils,
  ShoppingBag,
  GraduationCap,
  PencilRuler,
  Boxes,
  Truck,
  CheckCircle2,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { useQuote } from "@/lib/quoteContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1600&q=85";

const BLUEPRINT_IMG =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=85";

const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85";

const CATEGORIES = [
  {
    icon: Hotel,
    title: "Hotels & Hospitality",
    body: "Guest rooms, lobbies, F&B outlets — furniture, lighting, sanitary ware and decor as a single fit-out package.",
  },
  {
    icon: Building2,
    title: "Offices & Coworking",
    body: "Workstations, cabin furniture, meeting rooms, acoustic panels and full electrical + lighting sourcing.",
  },
  {
    icon: Home,
    title: "Residences & Villas",
    body: "End-to-end interior fit-out for apartments and villas — modular kitchen, wardrobes, tiles, sanitary, furniture.",
  },
  {
    icon: Utensils,
    title: "Restaurants & Cafés",
    body: "Custom tables, chairs, bar counters, decor lighting, kitchen equipment and signage — brand-consistent.",
  },
  {
    icon: ShoppingBag,
    title: "Retail & Showrooms",
    body: "Display fixtures, shelving, cladding, mannequins and lighting for retail chains and single-brand outlets.",
  },
  {
    icon: GraduationCap,
    title: "Institutional",
    body: "Schools, clinics, offices and clubs — bulk-quantity furniture and fittings with QC on every batch.",
  },
];

const HUBS = [
  {
    city: "Yiwu",
    role: "Primary hub",
    tags: "Furniture · Decor · Lighting · Textiles · Hardware",
    body: "Our home ground. Fastest turnaround, best pricing on bulk furniture, decor, soft furnishings and small building fixtures.",
    accent: true,
  },
  {
    city: "Foshan",
    role: "Specialist sourcing",
    tags: "Tiles · Sanitary Ware · Marble · Bathroom Fittings",
    body: "China's largest cluster for ceramic tiles, sanitary ware, faucets and marble. We ship directly from Foshan factories.",
  },
  {
    city: "Guangzhou",
    role: "Specialist sourcing",
    tags: "Furniture · Lighting · Curtain fabrics · Fashion",
    body: "Premium furniture and designer lighting. Curtain and upholstery fabrics for high-end residential and hospitality.",
  },
];

const JOURNEY = [
  { icon: PencilRuler, title: "Brief & Design", body: "Concept, mood boards, technical drawings, 3D visualisation and BoQ." },
  { icon: Boxes, title: "Sourcing & QC", body: "Factory shortlisting across Yiwu, Foshan, Guangzhou. Sample approval + on-floor QC." },
  { icon: Truck, title: "Ship & Clear", body: "Consolidated container, customs handled at destination, door-to-door delivery." },
  { icon: CheckCircle2, title: "Install-ready", body: "Room-wise packing lists, install instructions, and after-sales follow-up." },
];

export default function ProjectsPage() {
  const { openQuote } = useQuote();
  return (
    <main data-testid="projects-page" className="bg-ink-50">
      <Navigation />

      {/* Hero */}
      <section
        data-testid="projects-hero"
        className="relative min-h-[68vh] flex items-end overflow-hidden bg-ink text-white pt-32 pb-16"
      >
        <img src={HERO_IMG} alt="Modern architecture" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="absolute inset-0 grain opacity-20" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-6">
            Projects · Turnkey delivery
          </p>
          <h1 className="font-heading font-black tracking-[-0.035em] leading-[0.9] text-5xl md:text-7xl lg:text-[100px] uppercase">
            Design.<br />
            Source.<br />
            <span className="text-stroke-light">Deliver.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            Full-scope furnishing and building projects — from concept drawings
            to install-ready containers — sourced primarily from Yiwu, with
            specialist inputs from Foshan and Guangzhou.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
            <div className="md:col-span-7">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                What we deliver
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
                Turnkey, end to end.
              </h2>
            </div>
            <p className="md:col-span-5 text-ink-500 text-base md:text-lg leading-relaxed">
              You give us a brief. We come back with drawings, a BoQ, a fixed
              price and a shipping schedule. One partner from paper to install.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <article
                  key={c.title}
                  data-testid={`project-category-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="group p-7 md:p-8 bg-ink-50 border border-ink-100 hover:border-signal hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-ink-100 text-ink-900 group-hover:border-signal group-hover:text-signal transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-6 font-heading font-bold text-xl md:text-2xl tracking-tight text-ink-900">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-ink-500 text-[14px] leading-relaxed">{c.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sourcing hubs */}
      <section className="bg-ink-50 py-14 md:py-28 border-y border-ink-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-12 md:mb-16">
            <div className="lg:col-span-7">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                Sourcing hubs
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl">
                Three cities.<br />
                <span className="text-stroke">One consolidated container.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/3] overflow-hidden bg-ink">
                <img src={BLUEPRINT_IMG} alt="Architectural blueprint on desk" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {HUBS.map((h) => (
              <article
                key={h.city}
                data-testid={`hub-${h.city.toLowerCase()}`}
                className={`p-7 md:p-8 border transition-colors ${
                  h.accent
                    ? "bg-ink text-white border-ink"
                    : "bg-white border-ink-100 hover:border-signal"
                }`}
              >
                <div
                  className={`font-mono text-[11px] uppercase tracking-[0.22em] ${
                    h.accent ? "text-signal" : "text-signal"
                  }`}
                >
                  {h.role}
                </div>
                <h3
                  className={`mt-2 font-heading font-black tracking-tight text-3xl md:text-4xl ${
                    h.accent ? "text-white" : "text-ink-900"
                  }`}
                >
                  {h.city}
                </h3>
                <p
                  className={`mt-4 font-mono text-[11px] uppercase tracking-[0.16em] ${
                    h.accent ? "text-white/60" : "text-ink-500"
                  }`}
                >
                  {h.tags}
                </p>
                <p
                  className={`mt-4 text-[14px] leading-relaxed ${
                    h.accent ? "text-white/75" : "text-ink-500"
                  }`}
                >
                  {h.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Journey / process */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-end mb-12 md:mb-16">
            <div className="md:col-span-7">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                Project journey
              </p>
              <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
                Brief to install,<br />
                <span className="text-stroke">without hand-offs.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-ink-500 text-base md:text-lg leading-relaxed">
              Same team owns your project from the first drawing to the last
              installed piece. No blame games between designer, sourcing agent
              and freight forwarder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
            {JOURNEY.map((j, i) => {
              const Icon = j.icon;
              return (
                <div
                  key={j.title}
                  data-testid={`journey-step-${i + 1}`}
                  className="p-7 md:p-8 bg-ink-50 border border-ink-100"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal">
                    Step {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="mt-5 w-11 h-11 flex items-center justify-center border border-ink-100 text-ink-900">
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-5 font-heading font-bold text-xl md:text-[22px] tracking-tight text-ink-900">
                    {j.title}
                  </h3>
                  <p className="mt-3 text-ink-500 text-[14px] leading-relaxed">{j.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Editorial visual break */}
      <section className="bg-ink text-white relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch">
          <div className="relative aspect-[4/3] md:aspect-auto">
            <img src={INTERIOR_IMG} alt="Modern interior fit-out" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink/70" />
          </div>
          <div className="p-10 md:p-16 flex flex-col justify-center relative">
            <div className="absolute inset-0 grain opacity-30" aria-hidden />
            <p className="relative font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              Why turnkey
            </p>
            <h2 className="relative font-heading font-black tracking-[-0.03em] leading-[0.95] text-3xl md:text-4xl lg:text-5xl">
              One container.<br />
              One invoice.<br />
              One partner.
            </h2>
            <p className="relative mt-6 max-w-md text-white/70 text-base leading-relaxed">
              Turnkey isn't just a sourcing shortcut — it's what removes cost
              leaks. Consolidated shipping, single customs entry, one QC pass,
              and every SKU labelled by room. Install crews thank us.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              Have a project?
            </p>
            <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Send us your brief.<br />
              <span className="text-stroke">We'll draw the container.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <button
              type="button"
              onClick={openQuote}
              data-testid="projects-cta-quote"
              className="inline-flex items-center gap-2 bg-signal hover:bg-signal-600 text-white px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Start a Project
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-white px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
